import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import tr from './translations/tr';
import nl from './translations/nl';

export const LANGUAGES = {
  TR: 'tr',
  NL: 'nl'
};

export const LANGUAGE_OPTIONS = [
  { code: LANGUAGES.TR, label: 'Türkçe', flag: '🇹🇷', short: 'TR' },
  { code: LANGUAGES.NL, label: 'Nederlands', flag: '🇳🇱', short: 'NL' }
];

const translations = {
  [LANGUAGES.TR]: tr,
  [LANGUAGES.NL]: nl
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem('app_language');
      if (saved && (saved === LANGUAGES.TR || saved === LANGUAGES.NL)) {
        return saved;
      }
    } catch (e) {
      console.warn('LocalStorage language read error:', e);
    }
    return LANGUAGES.TR;
  });

  const setLanguage = useCallback((lang) => {
    if (lang !== LANGUAGES.TR && lang !== LANGUAGES.NL) return;
    setLanguageState(lang);
    try {
      localStorage.setItem('app_language', lang);
      document.documentElement.lang = lang;
    } catch (e) {
      console.warn('LocalStorage language save error:', e);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === LANGUAGES.TR ? LANGUAGES.NL : LANGUAGES.TR);
  }, [language, setLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  /**
   * Çeviri fonksiyonu: t('section.key', 'Varsayılan', { param: 'değer' })
   */
  const t = useCallback((path, fallback = '', params = null) => {
    if (!path) return fallback || '';

    const keys = path.split('.');
    let current = translations[language] || translations[LANGUAGES.TR];

    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        // Hedef dilde bulunamadıysa Türkçe'ye bak
        let trCurrent = translations[LANGUAGES.TR];
        for (const trKey of keys) {
          if (trCurrent && typeof trCurrent === 'object' && trKey in trCurrent) {
            trCurrent = trCurrent[trKey];
          } else {
            trCurrent = null;
            break;
          }
        }
        current = trCurrent !== null ? trCurrent : (fallback || path);
        break;
      }
    }

    if (typeof current !== 'string') {
      return fallback || path;
    }

    // Parametreleri değiştir ({name} -> değer)
    if (params && typeof params === 'object') {
      let result = current;
      Object.entries(params).forEach(([paramKey, paramVal]) => {
        result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramVal));
      });
      return result;
    }

    return current;
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    toggleLanguage,
    t,
    isTR: language === LANGUAGES.TR,
    isNL: language === LANGUAGES.NL,
    options: LANGUAGE_OPTIONS
  }), [language, setLanguage, toggleLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Context dışında çağrılırsa güvenli fallback sağlar
    return {
      language: LANGUAGES.TR,
      setLanguage: () => {},
      toggleLanguage: () => {},
      t: (path, fallback = '') => fallback || path,
      isTR: true,
      isNL: false,
      options: LANGUAGE_OPTIONS
    };
  }
  return context;
};

export default LanguageContext;
